"use client";
import React from "react";
import { useMyContext } from "../../context";
import { notiModalStyle } from "../../MUIStyles";
import useNotification from "../../hooks/useNotification";
import {
  Box,
  IconButton,
  Modal,
  Slide,
  CircularProgress,
  Alert,
  Typography,
  Badge,
  Button,
} from "@mui/material";
import { X, Circle } from "lucide-react";
import Pagination from "../Pagination";
import useFilter from "../../hooks/useFilter";

const Notification = () => {
  const { data, error, isError, isLoading, handleMarkNoti } = useNotification();
  const { modalStates, handleCloseModal } = useMyContext();
  const { setFilter, setTablePage } = useFilter();
  // Get unread notifications count
  const unreadCount = data?.data?.list?.filter((n) => !n.seen).length || 0;

  // Format date to readable format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));
      return `${diffInMinutes} minutes ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hours ago`;
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    // Mark as read if it's unread
    if (!notification.seen) {
      try {
        await handleMarkNoti(notification.id);
        // Wait for the mark as read API call to complete
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
        // Optional: Show error message but still allow navigation
        // alert("Failed to mark notification as read");
      }
    }

    // Redirect based on action
    let redirectUrl = "";
    switch (notification.type) {
      case "DRIVER_REGISTERED":
        redirectUrl = `/accounts/profile/${notification.action_id}/driver`;
        break;
      case "CAR_CREATED":
      case "CAR_INSPECTION_CREATED":
        redirectUrl = `/vehicles/vehicle-info/${notification.action_id}`;
        break;
      case "PAYMENT_RECEIVED":
        redirectUrl = `/trips/trip-info/${notification.action_id}`;
        break;
      case "COMPLAINT_CREATED":
        redirectUrl = `/disputes/details/${notification.action_id}`;
        break;
      case "IDENTITY_VERIFICATION_CREATED":
        redirectUrl = `/accounts/profile/${notification.action_id}/passenger`;
        break;
      case "SOS_EMERGENCY_CREATED":
        redirectUrl = `/incidents`;
        break;
      default:
        console.log("No redirect for notification type:", notification.type);
        // Close notification modal
        handleCloseModal("noti");
        return;
    }

    // Close notification modal first
    handleCloseModal("noti");

    // Then navigate after a short delay to ensure modal closes
    setTimeout(() => {
      window.location.href = redirectUrl;
    }, 100);
  };

  return (
    <Modal
      open={modalStates.noti}
      onClose={() => handleCloseModal("noti")}
      sx={{ display: "flex", justifyContent: "flex-start" }}
      hideBackdrop={false}
    >
      <Slide direction="right" in={modalStates.noti} mountOnEnter unmountOnExit>
        <Box sx={notiModalStyle} className="overflow-hidden">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b border-border2">
            <div className="flex flex-col gap-1 items-start">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold">Notifications</h2>
                {unreadCount > 0 && (
                  <Badge
                    badgeContent={unreadCount}
                    color="primary"
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: "0.75rem",
                        height: "20px",
                        minWidth: "20px",
                      },
                    }}
                  />
                )}
              </div>
              <h4 className="text-sm text-gray-500">
                {unreadCount > 0
                  ? `${unreadCount} unread notifications`
                  : "All caught up"}
              </h4>
            </div>
            <IconButton onClick={() => handleCloseModal("noti")}>
              <X color="black" />
            </IconButton>
          </div>

          {/* Notifications Content */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              // Loading State
              <div className="flex justify-center items-center h-64">
                <CircularProgress />
              </div>
            ) : isError ? (
              // Error State
              <div className="p-6">
                <Alert severity="error" className="mb-4">
                  {error?.message || "Failed to load notifications"}
                </Alert>
                <Button
                  variant="outlined"
                  onClick={() => window.location.reload()}
                  fullWidth
                >
                  Try Again
                </Button>
              </div>
            ) : !data?.data?.list || data.data.list.length === 0 ? (
              // Empty State
              <div className="flex flex-col justify-center items-center h-64 p-6 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <X size={24} className="text-gray-400" />
                </div>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  No notifications yet
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  We'll notify you when something new arrives
                </Typography>
              </div>
            ) : (
              // Notifications List
              <div className="divide-y divide-gray-100">
                {data?.data?.list?.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 cursor-pointer transition-all duration-200 hover:bg-gray-50 ${
                      !notification.seen ? "bg-blue-50" : "bg-white"
                    }`}
                    dir="rtl"
                  >
                    <div className="flex items-start gap-3">
                      {/* Notification Status Indicator */}
                      <div className="flex flex-col items-center pt-1">
                        {!notification.seen ? (
                          <Circle
                            size={10}
                            fill="#3b82f6"
                            className="text-blue-500"
                          />
                        ) : (
                          <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                        )}
                      </div>

                      {/* Notification Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start gap-2">
                          <p
                            className={`text-sm font-medium ${
                              !notification.seen
                                ? "text-gray-900"
                                : "text-gray-700"
                            }`}
                          >
                            {notification.title}
                          </p>
                          <span className="text-xs text-gray-500 whitespace-nowrap flex-shrink-0">
                            {formatDate(notification.created_at)}
                          </span>
                        </div>

                        {/* Notification Body/Details */}
                        <div className="mt-1">
                          {notification.body?.driver_id && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              <span className="px-2 py-1 bg-gray-100 text-xs text-gray-600 rounded">
                                Driver ID: {notification.body.driver_id}
                              </span>
                              {notification.body.driver_name && (
                                <span className="px-2 py-1 bg-gray-100 text-xs text-gray-600 rounded">
                                  Driver: {notification.body.driver_name}
                                </span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Notification Type */}
                        <div className="mt-2">
                          <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                            {notification.type.replace(/_/g, " ")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pagination - Moved inside the Box */}
          <div className="p-4 border-t border-border2">
            <Pagination
              paginate={{
                pageNumber: data?.data?.current_page,
                totalPages: data?.data?.total_pages,
                hasNext: data?.data?.has_next,
              }}
              setFilter={setFilter}
              setTablePage={setTablePage}
            />
          </div>
        </Box>
      </Slide>
    </Modal>
  );
};

export default Notification;
